import type { api } from "@healthos/schemas";
import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useMemo, useState } from "react";
import { client, jsonOrThrow } from "../../api/healthos";

/**
 * Company scope for platform callers (ops:all_companies). The chrome-bar
 * switcher writes the selection here; every ops query reads it, includes
 * it in its queryKey, and passes ?companyId= so the server narrows the
 * tenant scope. Non-global callers never see the switcher and the value
 * stays null, which leaves their requests untouched.
 */

export type OpsCompany = api.OperationsCompanySummary;

interface OpsCompanyContextValue {
  companyId: string | null;
  setCompanyId: (id: string | null) => void;
}

const OpsCompanyContext = createContext<OpsCompanyContextValue>({
  companyId: null,
  setCompanyId: () => {},
});

export function OpsCompanyProvider({ children }: { children: ReactNode }) {
  const [companyId, setCompanyId] = useState<string | null>(null);
  const value = useMemo(() => ({ companyId, setCompanyId }), [companyId]);
  return <OpsCompanyContext.Provider value={value}>{children}</OpsCompanyContext.Provider>;
}

export function useOpsCompany(): OpsCompanyContextValue {
  return useContext(OpsCompanyContext);
}

/** Merge the active company scope into a query-param record. */
export function companyScopedQuery(
  companyId: string | null,
  params: Record<string, string> = {},
): Record<string, string> {
  return companyId ? { ...params, companyId } : params;
}

export function useOpsCompanies(enabled: boolean) {
  return useQuery({
    queryKey: ["operations", "companies"],
    enabled,
    queryFn: async () =>
      jsonOrThrow<{ companies: OpsCompany[] }>(
        await client().api.operations.companies.$get(),
        "GET /api/operations/companies",
      ),
  });
}
